import React, { useState, useEffect, useMemo } from 'react'
import LivePowerChart from '../../components/charts/LivePowerChart'
import SystemHealthChart from '../../components/charts/SystemHealthChart'

interface LiveMetric {
  id: string
  name: string
  value: number
  unit: string
  trend: 'up' | 'down' | 'stable'
  change: number
}

const LiveAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState<LiveMetric[]>([])
  const [isConnected, setIsConnected] = useState(true)

  useEffect(() => {
    const mockMetrics: LiveMetric[] = [
      {
        id: 'power',
        name: 'Total Power',
        value: 2450.5,
        unit: 'kW',
        trend: 'up',
        change: 5.2
      },
      {
        id: 'efficiency',
        name: 'System Efficiency',
        value: 94.2,
        unit: '%',
        trend: 'stable',
        change: 0.1
      },
      {
        id: 'irradiance',
        name: 'Solar Irradiance',
        value: 850,
        unit: 'W/m²',
        trend: 'down',
        change: -2.8
      },
      {
        id: 'temperature',
        name: 'Module Temperature',
        value: 42.5,
        unit: '°C',
        trend: 'up',
        change: 1.5
      }
    ]
    setMetrics(mockMetrics)

    // Simulate real-time updates
    const interval = setInterval(() => {
      setMetrics(prev => prev.map(metric => ({
        ...metric,
        value: metric.value + (Math.random() - 0.5) * 10,
        change: (Math.random() - 0.5) * 5
      })))
    }, 2000)

    return () => clearInterval(interval)
  }, [])

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↗️'
      case 'down': return '↘️'
      case 'stable': return '➡️'
      default: return '➡️'
    }
  }

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-600'
      case 'down': return 'text-red-600'
      case 'stable': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Live Analytics</h1>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <div key={metric.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{metric.name}</h3>
              <span className="text-lg">{getTrendIcon(metric.trend)}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {metric.value.toFixed(1)} {metric.unit}
            </p>
            <p className={`text-sm ${getTrendColor(metric.trend)}`}>
              {metric.change > 0 ? '+' : ''}{metric.change.toFixed(1)}% from last hour
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Real-time Power Generation ⚡
          </h3>
          <LivePowerChart />
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            System Health Monitor 💚
          </h3>
          <SystemHealthChart />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Live Event Stream</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <span className="text-sm text-gray-900 dark:text-white">
                Inverter INV-{String(i + 1).padStart(2, '0')} status update
              </span>
              <span className="text-xs text-gray-500">
                {new Date(Date.now() - i * 30000).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Performance Alerts</h3>
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded">
            <div className="text-yellow-600 mr-3">⚠️</div>
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Efficiency drop detected in Block 3
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                2 minutes ago
              </p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded">
            <div className="text-green-600 mr-3">✅</div>
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                All systems operating normally
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                5 minutes ago
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LiveAnalytics